import SupportToolManager from './support_tool_manager';
import loggingProxy, { supportToolManagerLog } from './logging_proxy';

export const LoadStatus = {
  INIT: 'init',
  LOADING: 'loading',
  LOADED: 'loaded',
  FAILED: 'failed',
} as const;
type LoadStatusKey = typeof LoadStatus[keyof typeof LoadStatus];

export default abstract class BaseSupportTool {
  supportToolManager: SupportToolManager;
  loadStatus: LoadStatusKey = LoadStatus.INIT;

  constructor(supportToolManager: SupportToolManager) {
    this.supportToolManager = supportToolManager;

    return loggingProxy(this, supportToolManagerLog);
  }

  open?(): void;

  abstract isEnabled(): boolean;
  abstract hide(): void;
  abstract show(): void;
  abstract name(): string;

  // Not directly callable, intended to be implemented by base classes
  protected initialize?(): Promise<void>;
  protected onInitialize?(): Promise<void>;
  protected abstract isReady(): boolean;

  safeInitialize = async () => {
    if (this.loadStatus === 'init') {
      this.loadStatus = 'loading';
      await this.initialize?.();
      await this.waitUntilReady();
      await this.onInitialize?.();
    } else {
      await this.waitUntilReady();
    }

    // If we got here, we didn't raise while waiting.
    this.loadStatus = 'loaded';
  };

  waitUntilReady = async (): Promise<void> => {
    if (this.loadStatus === 'failed') {
      throw new Error(`${this.name()} failed to load, not attempting again.`);
    } else if (this.loadStatus === 'loaded') {
      return;
    }

    for (let i = 0; i < 10; i++) {
      if (this.isReady()) {
        supportToolManagerLog(`${this.name()} is ready!`);
        return;
      }
      await this.delay(500);
    }

    this.loadStatus = 'failed';
    throw new Error(`Timed out waiting for ${this.name()} to be ready`);
  };

  private delay = (ms: number) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };
}
